// Menu weapon pictures: hand-shaded pixel side views (inventory, Hachi's shop, gunsmith).
// The geometry of a build still comes from gunsmith.json `draw` blocks (shapesFor in gunsmith-art.ts, so parts sit
// where they fit); this file adds the finish: a 1 px dark outline, 4-tone material ramps lit from the top left,
// ejection ports, rivets, serrations, grip stipple, rail teeth, wood grain, magazine ribs and a neon accent in the
// item's rarity colour that bleeds into the outline. Everything is drawn pixel by pixel on a whole-pixel grid.
// Anything it does not cover falls back to legacyWeaponArt().
import { GUNS, isModular, resolve } from '../../common/src/gunsmith';
import type { Item } from '../../common/src/rules';
import { RARITIES } from '../../common/src/data';
import { PALETTE } from './theme';
import { legacyWeaponArt, shapesFor } from './gunsmith-art';

type RGB = [number, number, number];
const hex = (h: string): RGB => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const mix = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t].map(Math.round) as RGB;
const INK = hex(PALETTE.midnight), PAPER = hex(PALETTE.offWhite);

/** Tones, lightest first: highlight, light, base, shade, deep. Derived from the material's base colour. */
type Ramp = [RGB, RGB, RGB, RGB, RGB];
const ramp = (base: RGB): Ramp => [mix(base, PAPER, 0.5), mix(base, PAPER, 0.2), base, mix(base, INK, 0.35), mix(base, INK, 0.6)];
const BASE: Record<string, RGB> = {
  steel: [138, 145, 168], dark: [74, 80, 104], poly: [62, 66, 88], wood: [170, 108, 62], can: [96, 102, 124],
  rail: [112, 118, 142], tan: [150, 132, 96], port: [14, 12, 22], brass: [226, 170, 70],
};
const RAMPS: Record<string, Ramp> = Object.fromEntries(Object.entries(BASE).map(([k, v]) => [k, ramp(v)]));
/** Light-emitting materials: flat with a brighter top row. glass = lenses, glow = pink brand light. */
const EMIT: Record<string, RGB> = { glass: [111, 211, 255], glow: hex(PALETTE.pink) };
const rarityRgb = (r: string): RGB => (r === 'common' ? hex(PALETTE.cyan) : hex(RARITIES.tiers[r as keyof typeof RARITIES.tiers]?.color ?? PALETTE.cyan));

interface Sh { t: 'r' | 'p' | 'c'; x?: number; y?: number; w?: number; h?: number; r?: number; pts?: number[][]; c: string; z: number }

function bbox(s: Sh): number[] {
  if (s.t === 'r') return [s.x!, s.y!, s.x! + s.w!, s.y! + s.h!];
  if (s.t === 'c') return [s.x! - s.r!, s.y! - s.r!, s.x! + s.r!, s.y! + s.r!];
  const xs = s.pts!.map(p => p[0]), ys = s.pts!.map(p => p[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

/** Small details laid over a receiver: ejection port, rivets, serrations, sights, accent stripe (cm space, z >= 7). */
function details(d: any): Sh[] {
  const out: Sh[] = [];
  const rect = (x: number, y: number, w: number, h: number, c: string, z = 8) => out.push({ t: 'r', x, y, w, h, c, z });
  const dot = (x: number, y: number, r: number, c: string, z = 8) => out.push({ t: 'c', x, y, r, c, z });
  const port = (x: number, y: number, w: number, h: number) => { rect(x, y, w, h, 'port', 8); rect(x + 0.4, y + 0.25, 1, h - 0.5, 'brass', 9); };
  const R = d.R, H = d.H;
  if (d.body === 'ak') {
    port(R * 0.4, H * 0.28, 6.2, 1.9);
    for (const x of [1.4, R - 2, R * 0.62]) dot(x, H * 0.42, 0.45, 'steel');
    rect(3, H * 0.62, R * 0.28, 0.5, 'tech'); // maker stripe
    rect(R - 5.5, H * 0.82, 0.7, 1.1, 'dark'); // rear sight leaf
    rect(2, H * 0.12, 4, 0.5, 'steel');
  } else if (d.body === 'pistol') {
    for (let i = 0; i < 5; i++) rect(R - 7.4 + i * 1.15, H * 0.5, 0.5, H * 0.38, 'port', 7); // slide serrations
    port(R * 0.38, H * 0.62, 5, 1.2);
    rect(1.6, H * 0.18, R * 0.42, 0.45, 'tech'); // frame accent
    rect(1, H, 1.2, 0.55, 'dark'); rect(R - 2.6, H, 1, 0.55, 'dark'); // sights
    dot(R * 0.7, H * 0.3, 0.4, 'steel');
  } else if (d.body === 'smg') {
    port(R * 0.42, H * 0.38, 5.4, 1.5);
    for (let i = 0; i < 5; i++) rect(3 + i * 1.7, H * 0.2, 0.55, H * 0.28, 'dark', 7);
    for (let i = 0; i < 4; i++) rect(R + 1.4 + i * 2.4, d.bore + 1.45, 0.7, 0.6, 'port', 7); // tube vents
    rect(2, H * 0.62, R * 0.3, 0.5, 'tech');
    dot(R - 2.2, H * 0.55, 0.45, 'steel');
  } else if (d.body === 'mac') {
    port(R * 0.5, H * 0.45, 4.6, 1.5);
    for (let i = 0; i < 6; i++) rect(2 + i * 1.5, H * 0.3, 0.55, H * 0.28, 'port', 7);
    rect(1.2, H * 0.78, R * 0.4, 0.5, 'tech');
    dot(R - 2, H * 0.7, 0.45, 'steel');
  } else if (d.body === 'coil') {
    out.push({ t: 'p', pts: [[0, 0.6], [-9, 0.9], [-10, H - 1.6], [0, H - 0.4]], c: 'dark', z: 2 }); // stock
    rect(-10.8, 0.6, 1, H - 1.8, 'poly', 2);
    rect(R + 0.8, d.bore - 2.7, 24.5, 5.4, 'dark', 3); // coil sleeve
    for (let x = R + 1.6; x < R + 24.5; x += 2.7) rect(x, d.bore - 2.7, 1.1, 5.4, 'glass', 6); // dense glowing rings
    rect(R - 2, d.bore - 1.2, 2.2, 2.4, 'glass', 6); // charge lamp
    rect(R * 0.7, H * 0.2, 5, 0.5, 'glow');
    dot(3, H * 0.3, 0.5, 'steel');
    rect(R * 0.4, H * 0.55, 6, 1.7, 'port', 8); rect(R * 0.4 + 0.5, H * 0.55 + 0.3, 1.1, 1.1, 'brass', 9); // vent port
    for (let i = 0; i < 5; i++) rect(4 + i * 1.5, H * 0.75, 0.6, 1.8, 'port', 7); // heat-sink ribs
    for (const x of [R - 3, R * 0.5, 7]) dot(x, H * 0.45, 0.45, 'steel');
    rect(R + 1.2, d.bore + 2.7, 23, 0.45, 'steel', 7); rect(R + 1.2, d.bore - 3.15, 23, 0.45, 'rail', 7); // sleeve rails
    rect(-10, H * 0.9, 9, 0.45, 'tech', 3); // stock accent
  }
  return out;
}

/** Rasterise `shapes` into canvas `cv` with the finish described at the top of this file. */
function paint(cv: HTMLCanvasElement, shapes: Sh[], accent: RGB) {
  const W = cv.width, Hh = cv.height, pad = 3;
  let x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
  for (const s of shapes) { const b = bbox(s); x0 = Math.min(x0, b[0]); y0 = Math.min(y0, b[1]); x1 = Math.max(x1, b[2]); y1 = Math.max(y1, b[3]); }
  const sc = Math.min((W - pad * 2) / (x1 - x0), (Hh - pad * 2) / (y1 - y0));
  const offX = (W - (x1 - x0) * sc) / 2, offY = (Hh - (y1 - y0) * sc) / 2;
  const X = (x: number) => offX + (x - x0) * sc, Y = (y: number) => Hh - (offY + (y - y0) * sc);
  const ord = shapes.slice().sort((a, b) => a.z - b.z);
  const id = new Int16Array(W * Hh).fill(-1);
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= W || y >= Hh ? -1 : id[y * W + x]);
  ord.forEach((s, n) => {
    if (s.t === 'r') {
      const l = Math.round(X(s.x!)), r = Math.max(l + 1, Math.round(X(s.x! + s.w!))), tp = Math.round(Y(s.y! + s.h!)), bt = Math.max(tp + 1, Math.round(Y(s.y!)));
      for (let y = tp; y < bt; y++) for (let x = l; x < r; x++) if (x >= 0 && y >= 0 && x < W && y < Hh) id[y * W + x] = n;
    } else if (s.t === 'c') {
      const cx = X(s.x!), cy = Y(s.y!), rad = Math.max(0.9, s.r! * sc);
      for (let y = Math.floor(cy - rad); y <= Math.ceil(cy + rad); y++) for (let x = Math.floor(cx - rad); x <= Math.ceil(cx + rad); x++)
        if (x >= 0 && y >= 0 && x < W && y < Hh && (x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2 <= rad * rad) id[y * W + x] = n;
    } else {
      const P = s.pts!.map(p => [X(p[0]), Y(p[1])]);
      const ys = P.map(p => p[1]), xs = P.map(p => p[0]);
      for (let y = Math.floor(Math.min(...ys)); y <= Math.ceil(Math.max(...ys)); y++) for (let x = Math.floor(Math.min(...xs)); x <= Math.ceil(Math.max(...xs)); x++) {
        if (x < 0 || y < 0 || x >= W || y >= Hh) continue;
        const px = x + 0.5, py = y + 0.5; let inside = false;
        for (let i = 0, j = P.length - 1; i < P.length; j = i++)
          if ((P[i][1] > py) !== (P[j][1] > py) && px < ((P[j][0] - P[i][0]) * (py - P[i][1])) / (P[j][1] - P[i][1]) + P[i][0]) inside = !inside;
        if (inside) id[y * W + x] = n;
      }
    }
  });
  // per-shape extent in pixels
  const ext = ord.map(() => ({ x0: 1e9, x1: -1, y0: 1e9, y1: -1, n: 0 }));
  for (let y = 0; y < Hh; y++) for (let x = 0; x < W; x++) { const s = id[y * W + x]; if (s < 0) continue; const e = ext[s]; e.x0 = Math.min(e.x0, x); e.x1 = Math.max(e.x1, x); e.y0 = Math.min(e.y0, y); e.y1 = Math.max(e.y1, y); e.n++; }
  const accRamp = ramp(accent);
  const g = cv.getContext('2d')!; g.imageSmoothingEnabled = false; g.clearRect(0, 0, W, Hh);
  const img = g.createImageData(W, Hh), a = img.data;
  const put = (x: number, y: number, c: RGB) => { const k = (y * W + x) * 4; a[k] = c[0]; a[k + 1] = c[1]; a[k + 2] = c[2]; a[k + 3] = 255; };
  const emissive = new Uint8Array(W * Hh); // pixel colour index for outline bleed
  const bleedC: RGB[] = [];
  for (let y = 0; y < Hh; y++) for (let x = 0; x < W; x++) {
    const s = id[y * W + x]; if (s < 0) continue;
    const sh = ord[s], e = ext[s], m = sh.c;
    const isMat = (nx: number, ny: number) => at(nx, ny) === s;
    // lit edge: nothing of ours above. A different shape drawn over us above is occlusion (shade), not light.
    const upId = at(x, y - 1), dnId = at(x, y + 1), lfId = at(x - 1, y), rtId = at(x + 1, y);
    const coveredAbove = upId >= 0 && upId !== s && ord[upId].z > sh.z && ext[upId].n > 6;
    const top = !isMat(x, y - 1) && !coveredAbove, top2 = isMat(x, y - 1) && !isMat(x, y - 2);
    const bot = !isMat(x, y + 1), bot2 = isMat(x, y + 1) && !isMat(x, y + 2);
    const lf = !isMat(x - 1, y), rt = !isMat(x + 1, y);
    const hgt = e.y1 - e.y0 + 1, wid = e.x1 - e.x0 + 1;
    let col: RGB;
    const acc = m === 'tech';
    if (acc) { col = top ? accRamp[0] : y === e.y0 + 1 ? accRamp[1] : accRamp[2]; emissive[y * W + x] = bleedC.push(accent); }
    else if (EMIT[m]) {
      const b = EMIT[m]; col = top ? mix(b, PAPER, 0.55) : lf && wid > 3 ? mix(b, PAPER, 0.25) : bot && hgt > 2 ? mix(b, INK, 0.3) : b;
      emissive[y * W + x] = bleedC.push(b);
    } else {
      const r = RAMPS[m] ?? RAMPS.dark;
      let t = 2;
      if (hgt >= 3 || wid >= 6) {
        if (top) t = 0; else if (top2 && hgt >= 6) t = 1; else if (bot) t = hgt >= 3 ? 4 : 3; else if (bot2 && hgt >= 7) t = 3; else if (rt && wid > 3) t = 3; else if (lf && wid > 3) t = 1;
        if (coveredAbove && t === 2) t = 3;
      } else t = top ? 1 : 2;
      col = r[t];
      // textures, by material and size
      const big = e.n >= 30;
      if (m === 'poly' && big && sh.z <= 4 && t === 2 && (x + y * 2) % 4 === 0) col = r[3]; // grip / stock stipple
      else if (m === 'wood' && big) { if ((y * 5 + ((x >> 3) * 3)) % 4 === 0 && t >= 2) col = r[3]; else if (((x * 3 + y * 11) % 17) === 0) col = r[1]; }
      else if (m === 'rail') { if (x % 2 === 0 && t >= 1) col = r[3]; }
      else if (m === 'dark' && sh.z === 3 && hgt >= 10 && wid <= 14 && (y - e.y0) % 4 === 3 && t === 2) col = r[3]; // magazine ribs
      else if (m === 'steel' && big && t === 2 && (x * 7 + y * 3) % 23 === 0) col = r[1]; // machined sparkle
      else if (m === 'can' && hgt >= 4 && t === 2 && x % 4 === 0) col = r[3]; // suppressor baffles
    }
    put(x, y, col);
  }
  // seams between neighbouring parts so the build reads as pieces: the lower layer's edge pixel is darkened
  for (let y = 0; y < Hh; y++) for (let x = 0; x < W; x++) {
    const s = id[y * W + x]; if (s < 0) continue;
    for (const [dx, dy] of [[1, 0], [0, 1]]) {
      const o = at(x + dx, y + dy); if (o < 0 || o === s) continue;
      const lo = ord[s].z < ord[o].z ? s : o, k = lo === s ? (y * W + x) : ((y + dy) * W + (x + dx));
      if (ext[lo].n < 14 || ext[lo === s ? o : s].n < 6) continue;
      if (EMIT[ord[lo].c] || ord[lo].c === 'tech' || ord[lo].c === 'port' || ord[lo].c === 'brass') continue;
      const m2 = mix([a[k * 4], a[k * 4 + 1], a[k * 4 + 2]], INK, 0.45); a[k * 4] = m2[0]; a[k * 4 + 1] = m2[1]; a[k * 4 + 2] = m2[2];
    }
  }
  // outline, with a neon bleed where it touches a light-emitting pixel
  const out = new Uint8Array(W * Hh);
  for (let y = 0; y < Hh; y++) for (let x = 0; x < W; x++) {
    if (id[y * W + x] >= 0) continue;
    let bleed = -1;
    const n4 = [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]];
    let solid = false;
    for (const [nx, ny] of n4) if (at(nx, ny) >= 0) { solid = true; const em = emissive[ny * W + nx]; if (em) bleed = em - 1; }
    if (solid) { out[y * W + x] = 1; put(x, y, bleed >= 0 ? mix(INK, bleedC[bleed], 0.4) : INK); }
  }
  g.putImageData(img, 0, 0);
}

/** The original Kenji P-9 Carom: the old sprite's chunky silhouette (units x 1.3 = cm, y up), drawn with
 *  gunsmith-scale detail (rects of 0.4-1 cm) so its pixel density matches the other guns. */
function p9Shapes(): Sh[] {
  const K = 1.3, S: Sh[] = [];
  const X = (x: number) => x * K, Y = (y: number) => -y * K;
  const r = (x: number, y: number, w: number, h: number, c: string, z: number) => S.push({ t: 'r', x, y: y - h, w, h, c, z }); // raw cm, y = top
  const dot = (x: number, y: number, rad: number, c: string, z: number) => S.push({ t: 'c', x, y, r: rad, c, z });
  const p = (pts: number[][], c: string, z: number) => S.push({ t: 'p', pts: pts.map(q => [X(q[0]), Y(q[1])]), c, z });
  p([[4, 4], [18, 3], [21, 4], [21, 7], [4, 7]], 'steel', 5); // slide
  p([[5, 7], [10, 7], [8, 12], [4, 12]], 'poly', 4); // grip
  p([[8, 7], [12, 7], [10, 9], [8, 9]], 'dark', 4); // frame under the slide
  r(X(10), Y(8), 6.4, 4, 'dark', 4); r(X(10) + 0.6, Y(8) - 0.6, 5.2, 2.6, 'port', 5); r(X(12.2), Y(8) - 0.5, 0.6, 1.9, 'steel', 6); // trigger guard and trigger
  r(X(11), Y(7.05), 3.4, 0.5, 'rail', 6); // frame rail
  r(X(19.3), Y(4.2), 1.1, 2.8, 'port', 7); r(X(21) - 0.7, Y(4), 0.8, 3.9, 'steel', 6); // bore and muzzle ring
  for (let i = 0; i < 6; i++) r(X(5.1) + i * 0.95, Y(4.1), 0.45, 2.7, 'port', 7); // rear serrations
  for (let i = 0; i < 3; i++) r(X(15.4) + i * 0.95, Y(4.1), 0.45, 2.4, 'port', 7); // front serrations
  r(X(11.4), Y(4.5), 4.0, 1.9, 'port', 7); r(X(11.4) + 0.5, Y(4.5) - 0.4, 1.2, 1.1, 'brass', 8); // ejection port
  r(X(4.4), Y(3.6) + 0.9, 1.5, 0.9, 'dark', 7); dot(X(4.4) + 0.5, Y(3.6) + 0.4, 0.25, 'glass', 9); dot(X(4.4) + 1.1, Y(3.6) + 0.4, 0.25, 'glass', 9); // rear sight dots
  r(X(18.3), Y(3) + 0.7, 0.9, 1.0, 'dark', 7); r(X(18.3) + 0.25, Y(3) + 1.5, 0.45, 0.7, 'glass', 9); // front sight with a cyan tip
  r(X(5.6), Y(3.1) + 0.4, 2.0, 0.8, 'dark', 6); r(X(5.6) + 0.6, Y(3.1) + 0.3, 0.6, 0.6, 'glow', 9); // hammer with a pink LED
  r(X(8.6), Y(7.2), 3.0, 0.5, 'steel', 8); // slide stop
  r(X(12), Y(4.9) + 0.0, 5.4, 0.45, 'tech', 8); // rarity stripe
  r(X(18), Y(7), 2.6, 2.4, 'dark', 4); // muzzle-side frame lug
  dot(X(6.4), Y(9.4), 0.35, 'steel', 8); dot(X(6.9), Y(11.1), 0.35, 'steel', 8); // grip screws
  r(X(4), Y(12) + 0.0, 5.4, 0.7, 'steel', 5); // magazine base plate
  return S;
}

/** The original Homer-7 Smart SMG: stock, boxy receiver, sensor hump, ribbed shroud with a cyan emitter, at gunsmith density (units x 1.35 = cm). */
function homerShapes(): Sh[] {
  const K = 1.35, S: Sh[] = [];
  const X = (x: number) => x * K, Y = (y: number) => -y * K;
  const r = (x: number, y: number, w: number, h: number, c: string, z: number) => S.push({ t: 'r', x, y: y - h, w, h, c, z });
  const dot = (x: number, y: number, rad: number, c: string, z: number) => S.push({ t: 'c', x, y, r: rad, c, z });
  const p = (pts: number[][], c: string, z: number) => S.push({ t: 'p', pts: pts.map(q => [X(q[0]), Y(q[1])]), c, z });
  p([[2, 4], [6, 5], [7, 8], [2, 10]], 'poly', 2); r(X(1), Y(4), 1.5, 8.1, 'dark', 2); // stock and butt plate
  r(X(3), Y(6.2), 4.4, 0.7, 'port', 3); r(X(3), Y(8.2), 4.0, 0.7, 'port', 3); // stock lightening slots
  r(X(2.2), Y(5.2) - 0.5, 4.0, 0.45, 'tech', 3); dot(X(1) + 0.7, Y(5), 0.3, 'steel', 8); dot(X(1) + 0.7, Y(9), 0.3, 'steel', 8);
  p([[7, 4], [19, 3], [22, 5], [21, 8], [8, 9]], 'dark', 5); // receiver
  r(X(12), Y(2), 9.4, 2.9, 'dark', 6); // sensor hump
  r(X(12) + 0.8, Y(2) - 0.5, 4.2, 0.5, 'glass', 8); dot(X(17.6), Y(2.7) - 0.6, 0.4, 'glass', 9); dot(X(18.6), Y(2.7) - 0.6, 0.4, 'glass', 9); dot(X(19.4), Y(2.7) - 0.6, 0.3, 'glow', 9); // lenses and sensor LEDs
  r(X(8), Y(4) + 0.0, 4.6, 0.7, 'rail', 7); // top rail
  r(X(12), Y(5), 5.8, 2.3, 'port', 7); r(X(12) + 0.5, Y(5) - 0.5, 1.2, 1.3, 'brass', 8); // ejection port
  r(X(17), Y(5), 3.8, 0.45, 'tech', 8); // rarity strip
  r(X(16.4), Y(3), 1.6, 0.8, 'steel', 8); // cocking handle
  r(X(9) , Y(9), 4.0, 5.4, 'poly', 4); dot(X(10), Y(11), 0.35, 'steel', 8); // pistol grip
  r(X(13), Y(8), 4.0, 4.0, 'dark', 4); r(X(13) + 0.6, Y(8) - 0.6, 2.8, 2.7, 'port', 5); r(X(13.9), Y(8) - 0.5, 0.5, 1.6, 'steel', 6); // trigger guard and trigger
  r(X(15), Y(8), 5.4, 8.1, 'dark', 3); for (let i = 0; i < 3; i++) r(X(15) + 1.2, Y(9.2) - i * 1.7, 2.6, 0.9, 'glass', 4); // magazine and its charge window
  r(X(20), Y(4), 10.8, 6.8, 'dark', 6); r(X(20), Y(4), 10.8, 0.5, 'steel', 7); // boxy shroud
  for (let i = 0; i < 5; i++) r(X(21) + 0.6 + i * 1.6, Y(5), 0.7, 3.2, 'port', 8); // vent slots
  r(X(21), Y(8) + 0.2, 8.0, 0.6, 'rail', 7); r(X(21), Y(8) + 0.9, 8.0, 0.4, 'tech', 8); // under rail and accent
  r(X(28), Y(5), 6.8, 2.7, 'steel', 5); r(X(30), Y(4), 4.0, 5.4, 'dark', 6); r(X(33) + 0.2, Y(4.6), 0.9, 4.0, 'glass', 8); dot(X(33) + 0.6, Y(6), 0.3, 'glow', 9); // muzzle, cyan emitter
  return S;
}

function gunShapes(item: Item): { shapes: Sh[]; d: any } | null {
  const w = item.w as string;
  if (isModular(w)) { const pl = GUNS[w]; return { shapes: [...shapesFor(pl, resolve(pl, item.b ?? pl.defaults)), ...details(pl.draw)], d: pl.draw }; }
  if (w === 'pistol') return { shapes: p9Shapes(), d: {} };
  if (w === 'smart') return { shapes: homerShapes(), d: {} };
  if (w === 'charge') {
    const draw = { body: 'coil', R: 30, H: 6.6, bore: 3.4, grip: [9, 9.5, 12], top: 14 };
    const nodes = [{ path: 'barrel', id: 'coil', part: { type: 'barrel', draw: { s: 'coil', l: 26 } } }];
    return { shapes: [...shapesFor({ draw }, { nodes }), ...details(draw)], d: draw };
  }
  return null;
}

/** Draw the item's gun into a canvas. Returns false when nothing could be drawn (the caller then draws its own). */
export function weaponArt(cv: HTMLCanvasElement, item: Item | null | undefined): boolean {
  if (!item) return false;
  try {
    const built = gunShapes(item);
    if (built) { paint(cv, built.shapes, rarityRgb(item.r)); return true; }
  } catch (e) { console.warn('weapon art failed, using the old drawing', e); }
  return legacyWeaponArt(cv, item);
}

/** Melee pictures on a 48 px grid, hilt bottom-left, blade up-right, lit from the top left.
 *  Katana: wrapped hilt with diamond ito, gold tsuba and habaki, a wavy hamon on the lit edge. Neon tanto: short
 *  blade with a glowing core in the crew's cyan and a pink-wrapped grip. */
export function meleeArt(cv: HTMLCanvasElement, kind: 'katana' | 'tanto') {
  const W = cv.width, N = 48, k = W / N; // whole-number scale
  const g = cv.getContext('2d')!; g.imageSmoothingEnabled = false; g.clearRect(0, 0, cv.width, cv.height);
  const px = new Map<number, RGB>();
  const steel = ramp([196, 204, 220]), gold = ramp(hex(PALETTE.amber)), wrap = ramp(hex(PALETTE.pink)), hilt = ramp([54, 40, 72]);
  const cyan = hex(PALETTE.cyan);
  const tanto = kind === 'tanto';
  const L = tanto ? 36 : 55, bladeStart = tanto ? 14 : 18.5, tsubaAt = bladeStart - 4.5;
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    const dx = x - 4 + 0.5, dy = y - 43 + 0.5;
    const t = (dx - dy) / Math.SQRT2, u = (dx + dy) / Math.SQRT2; // t along the weapon, u across (negative = lit, upper-left side)
    let c: RGB | null = null;
    if (t >= 0 && t < 2.8 && Math.abs(u) <= 2.3 - (t < 0.8 ? 0.8 : 0)) c = u < -0.5 ? gold[1] : u > 0.9 ? gold[3] : gold[2]; // kashira
    else if (t >= 2.8 && t < tsubaAt && Math.abs(u) <= 2.1) {
      const dia = (Math.floor(t / 2.1) + Math.floor((u + 8) / 2.1)) % 2 === 0; // ito diamond wrap
      const r = dia ? wrap : hilt;
      c = u < -1.1 ? r[1] : u > 1 ? r[3] : r[2];
      if (Math.abs(u) <= 2.1 && (t - 2.8) % 4.2 < 0.9) c = r[0];
    } else if (t >= tsubaAt && t < tsubaAt + 2.4 && Math.abs(u) <= 4.3 - Math.abs(t - tsubaAt - 1.2) * 0.9) c = u < -1.5 ? gold[0] : u > 2 ? gold[3] : gold[2]; // tsuba
    else if (t >= tsubaAt + 2.4 && t < bladeStart && Math.abs(u) <= 2.2) c = u < -0.5 ? gold[1] : gold[3]; // habaki
    else if (t >= bladeStart && t <= L) {
      const tipT = (t - (L - (tanto ? 7 : 9))) / (tanto ? 7 : 9); // kissaki taper over the last stretch
      const hw = tanto ? 2.6 : 1.9, half = tipT > 0 ? hw * (1 - tipT * tipT) : hw;
      if (Math.abs(u) <= half) {
        const ham = -0.5 + Math.sin(t * 0.75) * 0.35; // hamon line, wavy
        if (u < -half + 0.7) c = steel[0]; // bright edge
        else if (tanto && Math.abs(u - 0.1) < 0.5) c = cyan; // glowing core
        else if (u < ham) c = steel[1];
        else if (u > half - 0.9) c = steel[4]; // spine
        else c = steel[2];
        if (!tanto && u < ham && u > ham - 0.55) c = steel[0]; // hamon highlight
      }
    }
    if (c) px.set(y * N + x, c);
  }
  const has = (x: number, y: number) => px.has(y * N + x);
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    const c = px.get(y * N + x); let col: RGB | null = null;
    if (c) col = c;
    else if (has(x - 1, y) || has(x + 1, y) || has(x, y - 1) || has(x, y + 1)) {
      const glow = tanto && [[-1, 0], [1, 0], [0, -1], [0, 1]].some(([a, b]) => { const n = px.get((y + b) * N + x + a); return n && n[0] === cyan[0] && n[1] === cyan[1]; });
      col = glow ? mix(INK, cyan, 0.4) : INK;
    }
    if (col) { g.fillStyle = `rgb(${col[0]},${col[1]},${col[2]})`; g.fillRect(x * k, y * k, k, k); }
  }
}
